/**
 * Hey Church design tokens, transcribed from design-plan/_ds/.../tokens/*.css.
 * Colour palette is unchanged; the typeface is Figtree (replaces licensed Moderat 1:1 by weight).
 * No radii, no shadows — the brand is square and flat; the app prototype uses radius 22 only
 * on cards inside the mobile shell (`radius.card`).
 */

export const palette = {
  carmine: { 999: "#9D022A", 900: "#B10834", 800: "#C60C3C", 700: "#D71043", 600: "#E8124A", 500: "#FC1F58", 400: "#FF3F68", 300: "#FF637D", 200: "#FF879B", 100: "#FFAFBC", 50: "#FFD7DD" },
  corral: { 999: "#A73525", 900: "#BE3F2D", 800: "#D84B36", 700: "#E7513B", 600: "#F55740", 500: "#FF5D44", 400: "#FF755D", 300: "#FE8E79", 200: "#FDAE9F", 100: "#FDCEC4", 50: "#F9EAEA" },
  yellow: { 999: "#E7660F", 900: "#FD7C26", 800: "#FF982B", 700: "#FFA82E", 600: "#FFBB32", 500: "#FFC837", 400: "#FFD044", 300: "#FFDA61", 200: "#FFE48D", 100: "#FFEEB9", 50: "#FFF9E4" },
  pine: { 999: "#002B37", 900: "#003544", 800: "#004A5B", 700: "#135E70", 600: "#247286", 500: "#318197", 400: "#5194A9", 300: "#6DA9BC", 200: "#85C4D1", 100: "#9AD3DF", 50: "#BCE8F3" },
  indigo: { 999: "#041D46", 900: "#0B254E", 800: "#133666", 700: "#1A3F73", 600: "#23487E", 500: "#2A5086", 400: "#416599", 300: "#5C7DAE", 200: "#7B97C0", 100: "#A3B7D3", 50: "#C7D6E9" },
  violet: { 999: "#401B49", 900: "#4C2556", 800: "#5F2E6C", 700: "#6D3376", 600: "#7D3A7F", 500: "#883F86", 400: "#995596", 300: "#AB70A9", 200: "#C38DC0", 100: "#D7B3D6", 50: "#EBD1E9" },
  beige: { 999: "#6B514D", 900: "#7E635F", 800: "#8E716C", 700: "#9C7D78", 600: "#B08D87", 500: "#C8A49F", 400: "#D8B9B4", 300: "#E4CAC6", 200: "#F5DEDA", 100: "#F7E7E6", 50: "#F5EFEF" },
  grey: { 999: "#2A2E30", 900: "#3B4246", 800: "#4C575B", 700: "#5A686D", 600: "#66757B", 500: "#79898F", 400: "#89989F", 300: "#A2AFB5", 200: "#BFC6C9", 100: "#DBDCDD", 50: "#F1F1F1" },
  white: "#FFFFFF",
  black: "#000000",
} as const;

/**
 * App theme, transcribed from the Hey Life App prototype's `--hl-*` layer
 * (`design-plan/Hey Life App.dc.html`, `[data-hl-theme]`). The prototype defaults to `dark`
 * and every approved screenshot shows it; `light` is the prototype's alternate (square, flat).
 * The Daily Word card follows the `data-hl-coral="white"` treatment seen in the screenshots:
 * white type and a white button on the coral field.
 */
export const appThemes = {
  dark: {
    bg: "#0B1622",
    surface: "#0E2440",
    surface2: "#15315A",
    text: "#FFFFFF",
    muted: "#BFC6C9",
    actionBg: "#FFFFFF",
    actionFg: "#0B1622",
    line: "rgba(255,255,255,.1)",
    lineStrong: "rgba(255,255,255,.25)",
    track: "rgba(255,255,255,.09)",
    tab: "#0B1622",
    overlay: "rgba(0,0,0,.62)",
    radius: { card: 22, small: 14, pill: 999 },
    dailyWord: { bg: "#FF755D", fg: "#FFFFFF", muted: "rgba(255,255,255,.8)", line: "rgba(255,255,255,.45)", btnBg: "#FFFFFF", btnFg: "#A73525", kicker: "#FFFFFF" },
    /** Tinted type on dark surfaces (`--hl-*-t`). */
    indigoT: "#7B97C0",
    pineT: "#6DA9BC",
    violetT: "#AB70A9",
    carmineT: "#FF637D",
    coralT: "#FF755D",
  },
  light: {
    bg: "#F5EFEF",
    surface: "#FFFFFF",
    surface2: "#F7E7E6",
    text: "#0B1622",
    muted: "#66757B",
    actionBg: "#0B1622",
    actionFg: "#F9EAEA",
    line: "rgba(11,22,34,.08)",
    lineStrong: "rgba(11,22,34,.2)",
    track: "rgba(11,22,34,.08)",
    tab: "#FFFFFF",
    overlay: "rgba(4,29,70,.45)",
    /** The prototype's light alternate is square; the app keeps the dark radii so the toggle only changes colour. */
    radius: { card: 22, small: 14, pill: 999 },
    /** The coral Daily Word is the brand moment on both schemes (customer screenshots, app icon). */
    dailyWord: { bg: "#FF755D", fg: "#FFFFFF", muted: "rgba(255,255,255,.8)", line: "rgba(255,255,255,.45)", btnBg: "#FFFFFF", btnFg: "#A73525", kicker: "#FFFFFF" },
    indigoT: "#133666",
    pineT: "#004A5B",
    violetT: "#6D3376",
    carmineT: "#C60C3C",
    coralT: "#D84B36",
  },
} as const;
export type AppThemeName = keyof typeof appThemes;

/**
 * Coloured fields used for cards inside the app (prototype `--hl-indigo`, `--hl-pine`, …).
 * Type on them is the prototype's: paper on the deep hues, white on coral, dark partners on
 * the pale ones. `t` is the tinted type colour for the same hue on a dark surface; `btnFg` is
 * the label colour of a white button placed on the field (coral card → Corral 999, as in the
 * prototype's Daily Word button).
 */
export const fields = {
  carmine: { bg: "#C60C3C", fg: "#F9EAEA", muted: "rgba(249,234,234,.75)", t: "#FF637D", btnFg: "#9D022A" },
  corral: { bg: "#FF755D", fg: "#FFFFFF", muted: "rgba(255,255,255,.8)", t: "#FF755D", btnFg: "#A73525" },
  yellow: { bg: "#FF982B", fg: "#002B37", muted: "rgba(0,43,55,.7)", t: "#FFD044", btnFg: "#E7660F" },
  pine: { bg: "#004A5B", fg: "#F9EAEA", muted: "rgba(249,234,234,.75)", t: "#6DA9BC", btnFg: "#004A5B" },
  indigo: { bg: "#133666", fg: "#F9EAEA", muted: "rgba(249,234,234,.75)", t: "#7B97C0", btnFg: "#133666" },
  violet: { bg: "#6D3376", fg: "#F9EAEA", muted: "rgba(249,234,234,.75)", t: "#AB70A9", btnFg: "#6D3376" },
  beige: { bg: "#E4C7C2", fg: "#6B514D", muted: "rgba(107,81,77,.7)", t: "#D8B9B4", btnFg: "#6B514D" },
  grey: { bg: "#BFC6C9", fg: "#2A2E30", muted: "rgba(42,46,48,.7)", t: "#DBDCDD", btnFg: "#2A2E30" },
  /** Life-area tints from the prototype (`--hl-body`, `--hl-soul`, `--hl-spirit`). */
  body: { bg: "#D9B68C", fg: "#0B1622", muted: "rgba(11,22,34,.7)", t: "#D9B68C", btnFg: "#0B1622" },
  soul: { bg: "#F5DEDA", fg: "#0B1622", muted: "rgba(11,22,34,.7)", t: "#F5DEDA", btnFg: "#0B1622" },
  spirit: { bg: "#BFC6C9", fg: "#0B1622", muted: "rgba(11,22,34,.7)", t: "#BFC6C9", btnFg: "#0B1622" },
} as const;
export type FieldName = keyof typeof fields;

/** Semantic palette of the mobile app for one colour scheme (`appPalettes.dark` / `.light`). */
export interface AppPalette {
  bg: string;
  surface: string;
  surface2: string;
  text: string;
  muted: string;
  actionBg: string;
  actionFg: string;
  /** Secondary type on `actionBg` (pills, own chat bubbles). */
  actionMuted: string;
  /** Type on a `*T` tint used as a fill (e.g. a completed day on `pineT`). */
  onTint: string;
  line: string;
  lineStrong: string;
  track: string;
  tab: string;
  overlay: string;
  radius: { card: number; small: number; pill: number };
  dailyWord: { bg: string; fg: string; muted: string; line: string; btnBg: string; btnFg: string; kicker: string };
  indigoT: string;
  pineT: string;
  violetT: string;
  carmineT: string;
  coralT: string;
  surfacePage: string;
  paper: string;
  paperWarm: string;
  paperSoft: string;
  ink: string;
  inkMuted: string;
  mutedOnDark: string;
  lineOnDark: string;
  coral: string;
  carmine: string;
  carmineField: string;
  pine: string;
  indigo: string;
  violet: string;
  yellow: string;
  beige: string;
  okBg: string;
  okFg: string;
  warnBg: string;
  warnFg: string;
  noteBg: string;
  noteFg: string;
  danger: string;
  link: string;
  linkHover: string;
}
export type AppScheme = "dark" | "light";

/** Raw brand colours that do not change with the scheme (type on coloured fields, cards, mails). */
const brand = {
  paper: "#F9EAEA",
  paperWarm: "#F7E7E6",
  paperSoft: "#F5EFEF",
  ink: "#0B1622",
  mutedOnDark: "#BFC6C9",
  lineOnDark: "rgba(255,255,255,.1)",
  coral: "#FF755D",
  carmine: "#E81239",
  carmineField: "#C60C3C",
  pine: "#004A5B",
  indigo: "#133666",
  violet: "#6D3376",
  yellow: "#FF982B",
  beige: "#E4C7C2",
} as const;

/**
 * Semantic colours used by the mobile app, per scheme. Dark is the customer prototype's default;
 * light is its `--hl-*` light alternate. The palette has no status colours, so the dark scheme uses
 * the hue tints at low alpha and the light scheme the pale Hey Church steps.
 */
export const appPalettes: Record<AppScheme, AppPalette> = {
  dark: {
    ...appThemes.dark,
    ...brand,
    surfacePage: appThemes.dark.bg,
    inkMuted: appThemes.dark.muted,
    actionMuted: "#66757B",
    onTint: "#0B1622",
    okBg: "rgba(109,169,188,.18)",
    okFg: "#6DA9BC",
    warnBg: "rgba(255,99,125,.18)",
    warnFg: "#FF637D",
    noteBg: "rgba(255,200,55,.16)",
    noteFg: "#FFD98A",
    danger: "#FF637D",
    link: "#7B97C0",
    linkHover: "#A3B7D3",
  },
  light: {
    ...appThemes.light,
    ...brand,
    surfacePage: appThemes.light.bg,
    inkMuted: appThemes.light.muted,
    actionMuted: "rgba(249,234,234,.7)",
    onTint: "#F9EAEA",
    okBg: "#DFF3EA",
    okFg: "#003544",
    warnBg: "#FFD9DF",
    warnFg: "#9D022A",
    noteBg: "#FFEEB9",
    noteFg: "#8A3D06",
    danger: "#C60C3C",
    link: "#133666",
    linkHover: "#2A5086",
  },
};

/** Default palette (dark) for code that does not switch schemes (cards, landing pages). */
export const colors: AppPalette = appPalettes.dark;

/** Dimension colours (lightened for the dark admin theme, FR-ADM-09). */
export const dimensions = {
  pray: { light: "#E8124A", dark: "#FF637D" },
  grow: { light: "#133666", dark: "#7B97C0" },
  fam: { light: "#2A5086", dark: "#4F7FCB" },
  serve: { light: "#004A5B", dark: "#2E9DB3" },
  give: { light: "#6D3376", dark: "#B07CC0" },
  share: { light: "#C60C3C", dark: "#F0426E" },
  decide: { light: "#E8124A", dark: "#FF4D7A" },
  body: { light: "#9C7D78", dark: "#D8B9B4" },
} as const;

/** Theme surfaces (full-bleed field · panel behind photography · ink on colour). */
export const themes = {
  carmine: { surface: "#E8124A", panel: "#C60C3C", ink: "#F9EAEA", type: "#041D46", accent: "#FFAFBC" },
  corral: { surface: "#FF755D", panel: "#D84B36", ink: "#F9EAEA", type: "#133666", accent: "#FDAE9F" },
  yellow: { surface: "#FF982B", panel: "#E7660F", ink: "#F9EAEA", type: "#002B37", accent: "#FFEEB9" },
  pine: { surface: "#004A5B", panel: "#003544", ink: "#F9EAEA", type: "#F9EAEA", accent: "#247286" },
  indigo: { surface: "#133666", panel: "#0B254E", ink: "#F9EAEA", type: "#F9EAEA", accent: "#2A5086" },
  violet: { surface: "#6D3376", panel: "#5F2E6C", ink: "#F9EAEA", type: "#F9EAEA", accent: "#883F86" },
  beige: { surface: "#E4C7C2", panel: "#B08D87", ink: "#8E716C", type: "#6B514D", accent: "#F7E7E6" },
  grey: { surface: "#BFC6C9", panel: "#66757B", ink: "#66757B", type: "#2A2E30", accent: "#F1F1F1" },
} as const;
export type ThemeName = keyof typeof themes;

/**
 * Typography. Figtree 400/500/700/900 (+ italics) replace Moderat Regular/Medium/Bold/Black.
 * Source Serif 4 SemiBold Italic for pull quotes, Rock Salt for handwritten asides.
 * Family names below are the PostScript-style names registered with expo-font / @fontsource.
 */
export const fonts = {
  core: {
    regular: "Figtree_400Regular",
    regularItalic: "Figtree_400Regular_Italic",
    medium: "Figtree_500Medium",
    mediumItalic: "Figtree_500Medium_Italic",
    bold: "Figtree_700Bold",
    boldItalic: "Figtree_700Bold_Italic",
    black: "Figtree_900Black",
    blackItalic: "Figtree_900Black_Italic",
  },
  accent: "SourceSerif4_600SemiBold_Italic",
  hand: "RockSalt_400Regular",
  /** CSS font stacks for web. */
  css: {
    core: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    accent: "'Source Serif 4', 'Source Serif Pro', ui-serif, Georgia, 'Times New Roman', serif",
    hand: "'Rock Salt', cursive",
  },
} as const;

/** Design-system scale at 1× (specimen sheets). The mobile app uses `type` below (scaled). */
export const typeScale = {
  h1: { size: 180, leading: 216 },
  h2: { size: 156, leading: 180 },
  h3: { size: 144, leading: 168 },
  h4: { size: 108, leading: 120 },
  h5: { size: 96, leading: 108 },
  h6: { size: 72, leading: 96 },
  h7: { size: 48, leading: 60 },
  copy: { size: 36, leading: 48 },
  caption: { size: 24, leading: 36 },
} as const;

/** Mobile scale (prototype at 402 pt wide). Same proportions, bold carries the voice. */
export const type = {
  display: { size: 34, lineHeight: 38, weight: "bold" },
  title: { size: 26, lineHeight: 30, weight: "bold" },
  heading: { size: 20, lineHeight: 24, weight: "bold" },
  body: { size: 16, lineHeight: 22, weight: "regular" },
  bodyMedium: { size: 16, lineHeight: 22, weight: "medium" },
  small: { size: 14, lineHeight: 20, weight: "regular" },
  caption: { size: 12, lineHeight: 16, weight: "bold" },
  kicker: { size: 11, lineHeight: 14, weight: "bold", letterSpacing: 0.8 },
} as const;

export const tracking = { text: 0.005, display: 0.04 } as const;

export const space = { 4: 4, 8: 8, 12: 12, 16: 16, 20: 20, 24: 24, 32: 32, 40: 40, 49: 49, 60: 60, 72: 72, 96: 96 } as const;

/** Brand foundations are square; the app prototype's dark theme uses 22 / 14 and pills. */
export const radius = { none: 0, card: 22, small: 14, pill: 999 } as const;

export * from "./brand";
export * from "./color";

export const sizes = {
  buttonPrimary: 52,
  buttonSecondary: 48,
  buttonPill: 50,
  tabBar: 64,
  cardImage: 1080,
  certificate: { w: 1080, h: 1350 },
} as const;
